import {
  channelRoute,
  formatRoute,
  mentionRoute,
  resolveInstanceUrl,
  videoFramePath,
  SEARCH_FIRST_PAGE,
  type InstanceConfig,
  type LinkSite,
  type RichNode,
} from '@visious/core';
import type { QueueCandidate } from '../queue/model';

/**
 * R8's node tree, with every destination already resolved — the render-ready form of a `RichNode`.
 *
 * This was put inside `screens/watch/model.ts` because Watch was the only page with prose in it.
 * 2d is the second caller (a channel's About tab is a description and nothing else), so it lifts on the
 * same rule `pickImage`, `channelRouteOf`, `listing/` and `ui/Menu` lifted on — and on a sharper one
 * here: the alternative is one screen importing another screen's model, which is worse than either
 * duplicating it or moving it.
 */

/**
 * Whose page a link goes to, drawn as a mark in front of it the way YouTube marks its own.
 *
 * `visious` is the one that stays in the app; the rest are core's {@link LinkSite}, so a link out to
 * Twitch or to X says so in front of the reader rather than in a status bar they cannot see.
 *
 * `null` is not "unknown", and it comes in two shapes. A `#hashtag` or an `@mention` is prose rather
 * than an address, and marking those would put two glyphs into every sentence of a description that
 * thanks four people. A link to a site outside the ten is unmarked because there is nothing true to
 * say about it: it keeps its full address, which is the honest label for a place we cannot name.
 */
export type LinkMark = 'visious' | LinkSite | null;

/**
 * What a site is called, for the sentence on a marked link ("Opens on Twitch.").
 *
 * Here rather than in `strings/`, because these are names rather than copy: a brand is spelled the
 * same in every locale, and a second copy of the list in each language file is a list that drifts.
 * The sentence around them is translated; the words in it are not.
 */
export function siteName(mark: Exclude<LinkMark, 'visious' | null>): string {
  return SITE_NAMES[mark];
}

const SITE_NAMES: Record<LinkSite, string> = {
  youtube: 'YouTube',
  twitch: 'Twitch',
  patreon: 'Patreon',
  // The site renamed itself and its old links did not, so both hosts land here — and "X" is what it
  // is called now.
  x: 'X',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  bluesky: 'Bluesky',
  reddit: 'Reddit',
  discord: 'Discord',
};


/** A `RichNode` with its in-app href resolved, so `view.tsx` never touches `formatRoute`. */
export type RichNodeView =
  | { readonly t: 'text'; readonly s: string }
  | { readonly t: 'br' }
  | {
      readonly t: 'link';
      readonly s: string;
      readonly href: string;
      /** Never `visious`: a `link` is the arm for the destinations this build cannot open. */
      readonly mark: LinkSite | null;
    }
  | { readonly t: 'timestamp'; readonly s: string; readonly seconds: number }
  /** `href` is `null` when the node has no resolvable target — it renders as plain text, not a dead link. */
  | {
      readonly t: 'route';
      readonly s: string;
      readonly href: string | null;
      /** Only `visious` or nothing: a `route` is in-app by construction. */
      readonly mark: 'visious' | null;
    }
  /**
   * A video this app can open. `s` is the URL the author wrote and is only a **fallback**: the
   * renderer shows the video's title once `inlineMeta.ts` has the record, and puts the queue's own
   * button after it.
   */
  | {
      readonly t: 'video';
      readonly s: string;
      readonly href: string;
      readonly videoId: string;
      readonly startSec: number | null;
      readonly playlistId: string | null;
    }
  /** A channel linked by its id, so `s` is the URL until the channel's name is cached. */
  | { readonly t: 'channel'; readonly s: string; readonly href: string; readonly channelId: string };

/**
 * Resolves each node's destination once, here, rather than in the renderer. Four of the eight node
 * kinds carry an *identifier* rather than a URL (a hashtag's tag, a mention's channel or handle, a
 * video's id), and turning those into hrefs is exactly the "zero URL building" R13 forbids in a `.tsx`.
 */
export function toRichNodeViews(nodes: readonly RichNode[]): readonly RichNodeView[] {
  return nodes.map((node): RichNodeView => {
    switch (node.t) {
      case 'text':
      case 'br':
      case 'timestamp':
        return node;
      case 'link':
        return { t: 'link', s: node.s, href: node.href, mark: node.site ?? null };
      case 'video':
        return {
          t: 'video',
          s: node.s,
          href: formatRoute({
            kind: 'watch',
            videoId: node.videoId,
            startSec: node.startSec,
            // The list the author linked comes along: a "watch the whole series" link that dropped
            // its `list` would open the video on its own and lose what the link was for.
            playlistId: node.playlistId,
            index: null,
          }),
          videoId: node.videoId,
          startSec: node.startSec,
          playlistId: node.playlistId,
        };
      case 'channel':
        return { t: 'channel', s: node.s, href: node.path, channelId: node.channelId };
      case 'route':
        return { t: 'route', s: node.s, href: node.path, mark: 'visious' };
      case 'hashtag':
        return {
          t: 'route',
          s: node.s,
          href: formatRoute({ kind: 'hashtag', tag: node.tag, page: SEARCH_FIRST_PAGE }),
          mark: null,
        };
      case 'mention': {
        const route = mentionRoute(node);
        return { t: 'route', s: node.s, href: route === null ? null : formatRoute(route), mark: null };
      }
    }
  });
}

/**
 * A linked video as the queue's own row, so the button beside it is the same button a grid card
 * carries and edits the same list.
 *
 * **It needs the record, which is why it takes one.** A queue row draws a title, a channel, a picture
 * and a duration — a card already has all four, and a link in a description has none of them until
 * `inlineMeta.ts` fetches the video. So the button appears when the title does, rather than putting a
 * URL in the queue and calling it a title.
 *
 * The frame needs no request of its own: it is addressable from the id alone (`videoFramePath`), and
 * is resolved against the *current* instance here rather than stored, which is R14's rule for images.
 */
export function inlineQueueCandidate(input: {
  readonly videoId: string;
  readonly href: string;
  readonly title: string;
  readonly author: string | null;
  readonly authorId: string | null;
  readonly lengthSeconds: number;
  readonly config: InstanceConfig | null;
}): QueueCandidate {
  return {
    videoId: input.videoId,
    href: input.href,
    title: input.title,
    author: input.author,
    authorHref: input.authorId === null ? null : formatRoute(channelRoute(input.authorId)),
    thumbnailUrl:
      input.config === null ? null : resolveInstanceUrl(input.config, videoFramePath(input.videoId)),
    // `0` is what the record says when it did not say — the queue row's own formatter already
    // refuses to draw that as `0:00`.
    durationSec: input.lengthSeconds > 0 ? input.lengthSeconds : null,
  };
}
